/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, ArrowLeft, LogOut, Stethoscope, Heart, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pad(n) { return String(n).padStart(2, "0"); }
function isoDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

export default function CalendarView() {
  const { user, loading, logout } = useAuth();
  const { t, lang } = useLang();
  const location = useLocation();
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedDate, setSelectedDate] = useState(isoDate(new Date()));

  useTrackModuleView("calendar");

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  // Grid extended to full weeks (Monday-first)
  const firstWeekday = (monthStart.getDay() + 6) % 7; // Mon=0
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - firstWeekday);
  const lastWeekday = (monthEnd.getDay() + 6) % 7;
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(gridEnd.getDate() + (6 - lastWeekday));

  const fetchEvents = async () => {
    setLoadingData(true);
    try {
      const { data } = await axios.get(`${API}/calendar`, {
        withCredentials: true,
        params: { start: isoDate(gridStart), end: isoDate(gridEnd) },
      });
      setEvents(data.events || []);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      (map[e.date] ||= []).push(e);
    }
    return map;
  }, [events]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  // Build calendar grid cells
  const cells = [];
  const d = new Date(gridStart);
  while (d <= gridEnd) {
    cells.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }

  const todayIso = isoDate(new Date());
  const monthLabel = `${t.app.calendar.months[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const selectedEvents = eventsByDate[selectedDate] || [];

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2" data-testid="back-to-dashboard">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button onClick={logout} variant="ghost" size="sm" className="text-white/80" data-testid="calendar-logout">
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <CalendarDays size={14} /> {t.app.calendar.overline}
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.calendar.title}</h1>
            <p className="mt-1 text-[#5C6B5C]">{t.app.calendar.subtitle}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-[#5C6B5C]">
              <span className="inline-block w-3 h-3 rounded-full bg-[#1C7A3C]" /> {t.app.calendar.legend_health}
              <span className="inline-block w-3 h-3 rounded-full bg-pink-600 ms-3" /> {t.app.calendar.legend_birth}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar grid */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-[#E2E4DF] p-6">
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={() => setCursor(addMonths(cursor, -1))}
                data-testid="calendar-prev"
                aria-label={t.app.calendar.prev}
                className="w-10 h-10 rounded-full hover:bg-[#F0F0EB] grid place-items-center text-[#1C3A27]"
              >
                {lang === "ar" ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
              <div className="font-display text-xl font-bold text-[#1C3A27] capitalize">{monthLabel}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setCursor(new Date()); setSelectedDate(todayIso); }}
                  data-testid="calendar-today"
                  className="px-3 h-9 rounded-full text-xs font-semibold border border-[#E2E4DF] hover:bg-[#F0F0EB] text-[#1C3A27]"
                >
                  {t.app.calendar.today}
                </button>
                <button
                  onClick={() => setCursor(addMonths(cursor, 1))}
                  data-testid="calendar-next"
                  aria-label={t.app.calendar.next}
                  className="w-10 h-10 rounded-full hover:bg-[#F0F0EB] grid place-items-center text-[#1C3A27]"
                >
                  {lang === "ar" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                </button>
              </div>
            </div>
            {/* Days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {t.app.calendar.days_short.map((d, i) => (
                <div key={i} className="text-center text-xs font-bold uppercase tracking-wider text-[#5C6B5C] py-2">{d}</div>
              ))}
            </div>
            {/* Cells */}
            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell) => {
                const iso = isoDate(cell);
                const inMonth = cell.getMonth() === cursor.getMonth();
                const isToday = iso === todayIso;
                const isSelected = iso === selectedDate;
                const dayEvents = eventsByDate[iso] || [];
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedDate(iso)}
                    data-testid={`day-${iso}`}
                    className={`relative aspect-square rounded-xl text-sm border-2 transition-colors flex flex-col items-center justify-start p-1.5 ${
                      isSelected
                        ? "border-[#E05A3D] bg-[#E05A3D]/10"
                        : isToday
                          ? "border-[#1C3A27] bg-white"
                          : "border-transparent bg-white hover:bg-[#F0F0EB]"
                    } ${inMonth ? "text-[#1C3A27]" : "text-[#5C6B5C]/40"}`}
                  >
                    <span className={`font-semibold ${isToday ? "text-[#E05A3D]" : ""}`}>{cell.getDate()}</span>
                    {dayEvents.length > 0 && (
                      <div className="absolute bottom-1.5 flex items-center gap-0.5">
                        {dayEvents.slice(0, 3).map((e, i) => (
                          <span
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${e.kind === "health" ? "bg-[#1C7A3C]" : "bg-pink-600"}`}
                          />
                        ))}
                        {dayEvents.length > 3 && <span className="text-[8px] font-bold text-[#5C6B5C]">+{dayEvents.length - 3}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {loadingData && <div className="mt-4 text-center text-xs text-[#5C6B5C]">{t.app.common.loading}</div>}
          </div>

          {/* Side panel: selected day */}
          <aside className="rounded-2xl bg-white border border-[#E2E4DF] p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#5C6B5C] font-bold">
              <CalendarDays size={14} /> {selectedDate === todayIso ? t.app.calendar.today_label : selectedDate}
            </div>
            <div className="font-display text-2xl font-black text-[#1C3A27] mt-1">
              {new Date(selectedDate).getDate()} {t.app.calendar.months[new Date(selectedDate).getMonth()]}
            </div>
            {selectedEvents.length === 0 ? (
              <p className="mt-6 text-sm text-[#5C6B5C]">{t.app.calendar.no_events}</p>
            ) : (
              <ul className="mt-5 space-y-3">
                {selectedEvents.map((e) => {
                  const isHealth = e.kind === "health";
                  const Icon = isHealth ? Stethoscope : Heart;
                  const color = isHealth ? "#1C7A3C" : "#DB2777";
                  const bg = isHealth ? "bg-emerald-50" : "bg-pink-50";
                  return (
                    <li key={e.id} className={`rounded-xl ${bg} border border-[#E2E4DF] p-3.5`}>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: color + "22", color }}>
                          <Icon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#1C3A27] truncate">
                            {isHealth
                              ? (t.app.health.types[e.subtype] || e.subtype)
                              : t.app.repro.types.birth}
                          </div>
                          <div className="text-xs text-[#5C6B5C] mt-0.5 truncate">
                            <span className="font-mono font-semibold text-[#1C3A27]">{e.animal?.tag || "—"}</span>
                            {e.animal?.name && <span> · {e.animal.name}</span>}
                            {isHealth && e.title && <span> · {e.title}</span>}
                            {!isHealth && e.title && <span> · {e.title}</span>}
                          </div>
                          <Link
                            to={e.link}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#1C3A27] hover:text-[#E05A3D]"
                            data-testid={`event-open-${e.id}`}
                          >
                            {t.app.calendar.open_module} <ArrowRight size={11} />
                          </Link>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
